import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { PortalRole, SampleUser } from '@solara/sample-data';
import { generateSampleUsers } from '@solara/sample-data';
import { useSession } from './SessionContext';

const STORAGE_KEY_USER = 'solara.portal.userId';

const USERS: SampleUser[] = generateSampleUsers(22);
/** Pick a primary persona per role for the role-switcher default. */
const DEFAULT_BY_ROLE: Record<PortalRole, SampleUser> = (() => {
  const out = {} as Record<PortalRole, SampleUser>;
  (['CustomerAdmin', 'Accountant', 'Approver', 'Viewer'] as PortalRole[]).forEach((role) => {
    out[role] = (USERS.find((u) => u.role === role && u.status === 'active') ?? USERS[0]) as SampleUser;
  });
  return out;
})();

export const ROLE_LABEL: Record<PortalRole, string> = {
  CustomerAdmin: 'Customer Admin',
  Accountant: 'Accountant',
  Approver: 'Approver',
  Viewer: 'Viewer',
};

export type PortalCapability =
  | 'invoices.create'
  | 'invoices.submit'
  | 'invoices.approve'
  | 'invoices.archive.search'
  | 'inbox.review'
  | 'compliance.read'
  | 'spend.read'
  | 'vat.read'
  | 'recovery_audit.read'
  | 'settings.users.manage'
  | 'settings.security.manage'
  | 'settings.erp.manage'
  | 'settings.api_keys.manage';

const ROLE_CAPS: Record<PortalRole, Set<PortalCapability>> = {
  CustomerAdmin: new Set([
    'invoices.create',
    'invoices.submit',
    'invoices.approve',
    'invoices.archive.search',
    'inbox.review',
    'compliance.read',
    'spend.read',
    'vat.read',
    'recovery_audit.read',
    'settings.users.manage',
    'settings.security.manage',
    'settings.erp.manage',
    'settings.api_keys.manage',
  ]),
  Accountant: new Set([
    'invoices.create',
    'invoices.submit',
    'invoices.archive.search',
    'inbox.review',
    'compliance.read',
    'spend.read',
    'vat.read',
    'recovery_audit.read',
  ]),
  Approver: new Set([
    'invoices.approve',
    'invoices.archive.search',
    'compliance.read',
    'spend.read',
    'vat.read',
    'recovery_audit.read',
  ]),
  Viewer: new Set([
    'invoices.archive.search',
    'compliance.read',
    'spend.read',
    'vat.read',
  ]),
};

interface AuthCtx {
  users: SampleUser[];
  currentUser: SampleUser;
  setRole: (role: PortalRole) => void;
  setUser: (userId: string) => void;
  can: (cap: PortalCapability) => boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const [userId, setUserId] = useState<string>(() => {
    if (typeof window === 'undefined') return DEFAULT_BY_ROLE.CustomerAdmin.userId;
    const stored = window.localStorage.getItem(STORAGE_KEY_USER);
    if (stored && USERS.some((u) => u.userId === stored)) return stored;
    return DEFAULT_BY_ROLE.CustomerAdmin.userId;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY_USER, userId);
    }
  }, [userId]);

  const currentUser = useMemo(
    () => USERS.find((u) => u.userId === userId) ?? (USERS[0] as SampleUser),
    [userId],
  );

  const setUser = useCallback((id: string) => setUserId(id), []);
  const setRole = useCallback((role: PortalRole) => setUserId(DEFAULT_BY_ROLE[role].userId), []);
  // Capability gating uses the **real** signed-in user's role from the JWT.
  // We fall back to the demo persona only until /auth/me has resolved.
  const effectiveRole: PortalRole = (session.user?.role as PortalRole) ?? currentUser.role;
  const can = useCallback(
    (cap: PortalCapability) => (ROLE_CAPS[effectiveRole] ?? ROLE_CAPS.Viewer).has(cap),
    [effectiveRole],
  );

  const value = useMemo<AuthCtx>(
    () => ({ users: USERS, currentUser, setRole, setUser, can }),
    [currentUser, setRole, setUser, can],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth must be used inside <AuthProvider>');
  return c;
}

export function useCan(cap: PortalCapability): boolean {
  return useAuth().can(cap);
}
